import { Injectable, computed, signal } from '@angular/core';

import { KsAssessmentRecord, KsBasis } from '../kickstart/assessment-record.types';
import { GlossaryEntry } from './glossary';

/** The application whose record *In this case* reads, when a page has one. */
export interface GlossaryCase {
  /** How the page identifies the application — carried on a *Flag this*. */
  readonly application: string;
  readonly record: KsAssessmentRecord;
}

/** *In this case*, resolved: absent, applied without a basis, or the blocks. */
export type TermCase =
  | { readonly state: 'absent' }
  | { readonly state: 'no-detail' }
  | { readonly state: 'blocks'; readonly blocks: readonly KsBasis[] };

const ABSENT: TermCase = { state: 'absent' };
const NO_DETAIL: TermCase = { state: 'no-detail' };

function hasDetail(basis: KsBasis): boolean {
  return (
    Object.keys(basis.inputs ?? {}).length > 0 ||
    basis.ground != null ||
    (basis.reduction_log?.length ?? 0) > 0
  );
}

/**
 * DOC-80 UI-DA-80-02: *In this case* is the record's, never reconstructed.
 * Absent where the rule did not arise here; *no detail* where it arose with no
 * basis recorded (a run before the basis existed, a hold with no evidence);
 * otherwise one block per line the rule produced.
 */
export function termCase(entry: GlossaryEntry, record: KsAssessmentRecord | null): TermCase {
  if (!record || !entry.appliedIn) return ABSENT;
  switch (entry.appliedIn) {
    case 'amount': {
      if (!record.run) return ABSENT;
      const amount = record.basis?.amount;
      return amount && hasDetail(amount) ? { state: 'blocks', blocks: [amount] } : NO_DETAIL;
    }
    case 'receipts': {
      const receipts = record.basis?.receipts ?? [];
      return receipts.length ? { state: 'blocks', blocks: receipts } : ABSENT;
    }
    case 'hold':
      if (!record.hold) return ABSENT;
      return hasDetail(record.hold) ? { state: 'blocks', blocks: [record.hold] } : NO_DETAIL;
  }
}

interface Mark {
  readonly key: string;
  readonly el: Element;
}

/**
 * The terms layer's per-page state (FLOW-348, DOC-80 UI-DA-80-01, -05).
 *
 * Every `app-term` registers here while it is on the page, so the FIRST
 * occurrence of a term — first in the document, not first to render — carries
 * the question mark and every later one the dotted underline; and the foot
 * panel can list the page's own terms first. A page with an assessment record
 * sets {@link case} so a term's pop-up can show *In this case*.
 */
@Injectable({ providedIn: 'root' })
export class GlossaryPageService {
  private readonly marks = signal<readonly Mark[]>([]);

  /** The record *In this case* reads — null on a page that has none. */
  readonly case = signal<GlossaryCase | null>(null);

  /** The terms on the page, in document order, each once. */
  readonly pageKeys = computed(() => {
    const ordered = [...this.marks()].sort((a, b) => order(a.el, b.el));
    return [...new Set(ordered.map((m) => m.key))];
  });

  register(key: string, el: Element): void {
    this.marks.update((marks) => [...marks, { key, el }]);
  }

  unregister(el: Element): void {
    this.marks.update((marks) => marks.filter((m) => m.el !== el));
  }

  /** Whether `el` is the first occurrence of `key` on the page. */
  isFirst(key: string, el: Element): boolean {
    return this.marks().every((m) => m.key !== key || m.el === el || order(el, m.el) < 0);
  }
}

/** Negative where `a` comes before `b` in the document. */
function order(a: Element, b: Element): number {
  if (a === b) return 0;
  return a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1;
}
